// src/pages/payments/SubscribeCard.jsx
// F5 hotfix 2026-05-10 OPCIÓN A — form HTML real con action a Cloud
// Function HTTP. Reemplaza el approach callable JSON-RPC porque la
// arquitectura del widget Flow Suscripciones requiere un POST tradicional
// (la doc oficial muestra form.submit()).
//
// Flujo:
//   1. Backend persiste pendingSubscription { registerToken, returnOrigin, ... }
//   2. Frontend renderiza <form action=".../flowSubscribeCallback" method=POST>
//      con <input hidden name=token> + <div> donde monta el widget Flow JS
//   3. Widget user submit → handleCardSubscribed.then(data) →
//      setTimeout(form.submit(), 3000) (per doc oficial)
//   4. Browser hace POST nativo al endpoint Cloud Function
//   5. Endpoint llama getRegisterStatus + createSubscription + persiste
//   6. Endpoint responde 302 redirect a returnOrigin/pago/return?activated=true
//   7. Browser navega → PaymentReturn.jsx detecta query.activated → /dashboard
//
// Recovery: si user llega sin location.state (refresh), lee
// pendingSubscription del user doc.

import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../firebase';
import { useAuth } from '../../auth/AuthContext';

const FLOW_SCRIPT_SANDBOX = 'https://sandbox.flow.cl/app/elements/flow-1.1.0.min.js';
const FLOW_SCRIPT_PROD = 'https://www.flow.cl/app/elements/flow-1.1.0.min.js';

const FLOW_SUBSCRIBE_CALLBACK_URL =
  'https://southamerica-east1-mati-edge-elite.cloudfunctions.net/flowSubscribeCallback';

export default function SubscribeCard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  const stateData = location.state || {};
  const [token, setToken] = useState(stateData.token || null);
  const [isSandbox, setIsSandbox] = useState(
    typeof stateData.isSandbox === 'boolean' ? stateData.isSandbox : null
  );
  const [planId, setPlanId] = useState(stateData.planId || null);

  const [status, setStatus] = useState('init'); // init | loading_script | mounting | waiting_user | submitting | error
  const [errorMsg, setErrorMsg] = useState(null);
  const formRef = useRef(null);
  const handlerRegisteredRef = useRef(false);

  // Recovery: si state vacío, leer pendingSubscription del user doc.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (token && isSandbox !== null) return;

    let cancelled = false;
    (async () => {
      try {
        const userSnap = await getDoc(doc(firestore, 'users', user.uid));
        if (cancelled) return;
        const u = (userSnap.exists() && userSnap.data()) || {};
        const pending = u.pendingSubscription;
        if (!pending || !pending.registerToken) {
          setErrorMsg('No hay suscripción pendiente. Empezá desde el paywall.');
          setStatus('error');
          return;
        }
        setToken(pending.registerToken);
        setPlanId(pending.planId);
        setIsSandbox(u.flowEnv === 'sandbox');
      } catch (e) {
        if (cancelled) return;
        console.error('[SubscribeCard] recovery error:', e);
        setErrorMsg('No pudimos recuperar la suscripción. Prueba desde el paywall.');
        setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user, token, isSandbox, navigate]);

  // Cargar script Flow + montar widget.
  useEffect(() => {
    if (!token || isSandbox === null) return;
    if (status !== 'init') return;

    setStatus('loading_script');
    const scriptSrc = isSandbox ? FLOW_SCRIPT_SANDBOX : FLOW_SCRIPT_PROD;

    let scriptEl = document.querySelector(`script[src="${scriptSrc}"]`);
    if (scriptEl && window.Flow) {
      mountWidget();
      return;
    }

    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.src = scriptSrc;
      scriptEl.async = true;
      scriptEl.onload = mountWidget;
      scriptEl.onerror = () => {
        console.error('[SubscribeCard] script Flow no cargó:', scriptSrc);
        setErrorMsg('No pudimos cargar Flow. Revisa tu conexión y vuelve a intentar.');
        setStatus('error');
      };
      document.body.appendChild(scriptEl);
    } else {
      scriptEl.addEventListener('load', mountWidget);
    }

    function mountWidget() {
      if (!window.Flow) {
        setErrorMsg('Flow no se inicializó. Refresca la página.');
        setStatus('error');
        return;
      }
      try {
        setStatus('mounting');
        const flow = window.Flow();
        const elements = flow.elements();
        const subscribe = elements.create('subscribe', {
          style: { backgroundColor: '#f8f9fa' },
        });
        subscribe.mount('#subscribe-container', token);

        if (!handlerRegisteredRef.current) {
          handlerRegisteredRef.current = true;

          // Doc oficial Flow Suscripciones (developers.flow.cl/docs/suscripciones/register-card):
          //   flow.handleCardSubscribed(subscribe).then(function(data) {
          //     setTimeout(function() { form.submit(); }, 3000);
          //   });
          //
          // El SDK actual (flow-1.1.0.min.js) devuelve un thenable parcial:
          //   typeof === 'function', .then existe, .catch NO existe.
          // Usamos forma dual-arg .then(onFulfilled, onRejected) Promise/A+
          // mínimo para evitar TypeError del .catch missing.
          const handlerResult = flow.handleCardSubscribed(subscribe);
          console.log('[SubscribeCard] handleCardSubscribed shape:', {
            type: typeof handlerResult,
            hasThen: typeof handlerResult?.then === 'function',
            hasCatch: typeof handlerResult?.catch === 'function',
          });

          const onResolve = (data) => {
            console.log('[SubscribeCard] handleCardSubscribed resolved:', data);
            if (data && (data.error || data.errorCode || data.code)) {
              handleSubscribeError(data);
              return;
            }
            // Per doc oficial: 3 segundos antes del form.submit().
            // Le da tiempo a Flow de finalizar inscripción interna antes
            // de que el backend consulte getRegisterStatus.
            setStatus('submitting');
            setTimeout(() => {
              if (formRef.current) {
                console.log('[SubscribeCard] form.submit() → flowSubscribeCallback');
                formRef.current.submit();
              } else {
                console.error('[SubscribeCard] formRef is null');
                setErrorMsg('Error interno al enviar el formulario.');
                setStatus('error');
              }
            }, 3000);
          };

          const onReject = (err) => handleSubscribeError(err);

          if (handlerResult && typeof handlerResult.then === 'function') {
            handlerResult.then(onResolve, onReject);
          } else {
            console.warn('[SubscribeCard] handler no thenable, fallback sincrónico:', handlerResult);
            onResolve(handlerResult);
          }
        }
        setStatus('waiting_user');
      } catch (e) {
        console.error('[SubscribeCard] mount widget error:', e);
        setErrorMsg('No pudimos mostrar el formulario de tarjeta.');
        setStatus('error');
      }
    }
  }, [token, isSandbox, status]);

  function handleSubscribeError(e) {
    console.error('[SubscribeCard] handleCardSubscribed rejected:', e);
    setErrorMsg('No pudimos registrar la tarjeta. ¿Querés intentar con otra?');
    setStatus('error');
    handlerRegisteredRef.current = false;
  }

  // ─── Renders ───
  if (status === 'error') {
    return (
      <div className="mobile-root">
        <Helmet><title>Hubo un problema · Edge Elite</title></Helmet>
        <section className="screen" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
            No pudimos completar el registro
          </h2>
          <p style={{ fontSize: 14, color: 'var(--muted)', marginTop: 8, lineHeight: 1.5 }}>
            {errorMsg || 'Algo falló al registrar tu tarjeta.'}
          </p>
          <button
            type="button"
            onClick={() => navigate('/paywall')}
            className="btn-primary accent"
            style={{ marginTop: 24, padding: '12px 24px' }}
          >
            Volver al paywall
          </button>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 16 }}>
            Tu tarjeta no fue cobrada.
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="mobile-root">
      <Helmet><title>Registrar tarjeta · Edge Elite</title></Helmet>
      <section className="screen" style={{ paddingTop: 24, paddingBottom: 32 }}>
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 4 }}>
            Registrá tu tarjeta
          </h2>
          <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>
            🔒 Pago seguro a través de Flow.cl. No te cobramos nada hasta el día 3.
            Tu trial de 2 días empieza ahora.
          </p>
        </div>

        {(status === 'init' || status === 'loading_script' || status === 'mounting') && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
            <div style={{ fontSize: 13 }}>Cargando formulario seguro…</div>
          </div>
        )}

        {status === 'submitting' && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
            <div style={{ fontSize: 13 }}>Procesando suscripción…</div>
            <div style={{ fontSize: 11, marginTop: 8 }}>No cierres esta ventana.</div>
          </div>
        )}

        {/*
          Form HTML real con action a la Cloud Function HTTP. El widget Flow
          monta sobre el div interno y, tras handleCardSubscribed.then() +
          setTimeout(3000), llamamos formRef.current.submit() — submit nativo
          del browser, NO fetch. Backend responde 302 a returnOrigin/pago/return.
        */}
        <form
          ref={formRef}
          id="formSubscribe"
          action={FLOW_SUBSCRIBE_CALLBACK_URL}
          method="POST"
          style={{
            display: status === 'waiting_user' || status === 'mounting' || status === 'submitting'
              ? 'block'
              : 'none',
          }}
        >
          <input type="hidden" name="token" value={token || ''} />
          <div
            id="subscribe-container"
            style={{
              minHeight: 250,
              background: '#f8f9fa',
              borderRadius: 12,
              padding: 4,
            }}
          />
        </form>

        {planId && (
          <p style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', marginTop: 16 }}>
            Plan: {planId} · 2 días gratis · Después se renueva automático
          </p>
        )}
      </section>
    </div>
  );
}
